import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import {
  ACADEMY_NEWS,
  AcademyLesson,
  AcademyNews,
  AcademyTopic,
} from '../../../academy/academy-catalog';
import { AcademyService, CertificateState, LessonStatus } from '../../../academy/academy.service';
import { SegmentOption } from '../../../components/segmented-control/segmented-control.component';
import { UserService } from '../../../services/user.service';
import { ORBI_IDLE } from '../../../components/orbi-lab/orbi-lab.component';

type AcademyFilter = 'all' | 'open' | 'completed' | 'hints';

interface LessonRow {
  lesson: AcademyLesson;
  title: string;
  subtitle: string;
  status: LessonStatus;
  isNew: boolean;
}

interface TopicTile {
  topic: AcademyTopic;
  /** „01“ … „08“ — die Themen bauen aufeinander auf. */
  number: string;
  title: string;
  description: string;
  completed: number;
  total: number;
  /** done = alle Lektionen, current = erstes offenes Thema, upcoming = danach. */
  state: 'done' | 'current' | 'upcoming';
  /** Ein Strich je Lektion in Reihenfolge, `true` = abgeschlossen. */
  ticks: boolean[];
}

interface NewsRow {
  news: AcademyNews;
  title: string;
  text: string;
  lesson?: AcademyLesson;
}

interface CertRow {
  state: CertificateState;
  title: string;
  description: string;
  missingTitle: string;
}

const FILTERS: AcademyFilter[] = ['all', 'open', 'completed', 'hints'];
const ORBI_SCRIPT = [ORBI_IDLE, 'giggle-bounce', ORBI_IDLE, ORBI_IDLE, 'hop-laugh', ORBI_IDLE];

/**
 * Academy-Dashboard (`/academy`): Begrüßung mit Lernstand, „Weiterlernen“,
 * Suche und Filter, Themen, Neuigkeiten und Zertifikate. `?view=hints`
 * öffnet die Übersicht aller Portalhinweise (Link „Alle Hinweise ansehen“
 * in der Hinweis-Box), `?q=` eine Suche.
 *
 * Alles Abgeleitete wird bei jeder Änderung von Inhalt/Fortschritt EINMAL
 * berechnet und als Feld gehalten — keine Getter, die Arrays bauen
 * (CLAUDE.md Regel 18).
 */
@Component({
  selector: 'app-academy-home',
  templateUrl: './academy-home.component.html',
  styleUrls: ['./academy-home.component.scss'],
})
export class AcademyHomeComponent implements OnInit, OnDestroy {
  ready = false;
  name = '';
  query = '';
  filter: AcademyFilter = 'all';
  filterOptions: SegmentOption[] = [];
  readonly orbiScript = ORBI_SCRIPT;

  overall = { completed: 0, started: 0, open: 0, total: 0, percent: 0 };
  next: LessonRow | null = null;
  nextSummary = '';
  topics: TopicTile[] = [];
  news: NewsRow[] = [];
  certs: CertRow[] = [];
  results: LessonRow[] = [];

  private readonly destroy$ = new Subject<void>();

  constructor(
    public academy: AcademyService,
    private route: ActivatedRoute,
    private router: Router,
    private translate: TranslateService,
    private userService: UserService,
  ) {}

  get searching(): boolean {
    return !!this.query.trim() || this.filter !== 'all';
  }

  ngOnInit(): void {
    this.academy.ensureContent();
    this.academy.loadProgress();
    this.buildFilterOptions();
    this.translate.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(() => this.buildFilterOptions());

    this.route.queryParamMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const view = params.get('view') as AcademyFilter | null;
      this.filter = view && FILTERS.includes(view) ? view : 'all';
      this.query = params.get('q') ?? '';
      this.rebuild();
    });

    combineLatest([
      this.academy.content$.pipe(filter((c) => !!c)),
      this.academy.progress$,
      this.academy.progressLoaded$.pipe(filter(Boolean)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ready = true;
        this.rebuild();
      });

    this.userService.me$.pipe(takeUntil(this.destroy$)).subscribe((me) => {
      this.name = (me?.name ?? '').trim().split(/\s+/)[0] ?? '';
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onQuery(value: string): void {
    this.query = value;
    this.syncUrl();
    this.rebuild();
  }

  onFilter(value: string): void {
    this.filter = (FILTERS as string[]).includes(value) ? (value as AcademyFilter) : 'all';
    this.syncUrl();
    this.rebuild();
  }

  resetSearch(): void {
    this.query = '';
    this.filter = 'all';
    this.syncUrl();
    this.rebuild();
  }

  trackTopic(_: number, t: TopicTile): string {
    return t.topic.id;
  }

  trackRow(_: number, r: LessonRow): string {
    return r.lesson.id;
  }

  private syncUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: this.filter === 'all' ? null : this.filter, q: this.query.trim() || null },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private buildFilterOptions(): void {
    this.filterOptions = FILTERS.map((f) => ({ value: f, label: this.translate.instant(`academy.filter.${f}`) }));
  }

  private row(lesson: AcademyLesson): LessonRow {
    return {
      lesson,
      title: this.academy.lessonTitle(lesson.id),
      subtitle: this.academy.topicText(lesson.topic).title,
      status: this.academy.lessonStatus(lesson.id),
      isNew: this.academy.isNew(lesson),
    };
  }

  private rebuild(): void {
    if (!this.academy.content$.value) return;
    this.overall = this.academy.overallStats();

    const next = this.academy.nextLesson();
    this.next = next ? this.row(next) : null;
    this.nextSummary = next ? (this.academy.lessonText(next.id)?.summary ?? '') : '';

    let currentSeen = false;
    this.topics = this.academy.topics().map((topic, i) => {
      const text = this.academy.topicText(topic.id);
      const stats = this.academy.topicStats(topic.id);
      const done = stats.total > 0 && stats.completed === stats.total;
      const state: TopicTile['state'] = done ? 'done' : currentSeen ? 'upcoming' : 'current';
      if (!done) currentSeen = true;
      return {
        topic,
        number: String(i + 1).padStart(2, '0'),
        title: text.title,
        description: text.description,
        completed: stats.completed,
        total: stats.total,
        state,
        ticks: this.academy.lessonsOfTopic(topic.id).map((l) => this.academy.lessonStatus(l.id) === 'completed'),
      };
    });

    const content = this.academy.content$.value;
    this.news = ACADEMY_NEWS.map((n) => ({
      news: n,
      title: content.news[n.id]?.title ?? n.id,
      text: content.news[n.id]?.text ?? '',
      lesson: n.lessonId ? this.academy.lessonById(n.lessonId) : undefined,
    }));

    this.certs = this.academy.certificates().map((state) => {
      const text = this.academy.certificateText(state.cert.id);
      return {
        state,
        title: text.title,
        description: text.description,
        missingTitle: state.missingPrerequisite ? this.academy.certificateText(state.missingPrerequisite).title : '',
      };
    });

    const base = this.filter === 'hints' ? this.academy.hintLessons() : this.academy.search(this.query);
    const searched = this.filter === 'hints' && this.query.trim()
      ? base.filter((l) => this.academy.search(this.query).includes(l))
      : base;
    this.results = searched
      .map((l) => this.row(l))
      .filter((r) =>
        this.filter === 'open' ? r.status !== 'completed' : this.filter === 'completed' ? r.status === 'completed' : true,
      );
  }
}
